/**
 * Domain types for the auction engine.
 *
 * Implements docs/spec/auction-state-and-event-spec.md. Nothing in this package
 * performs I/O, reads the clock, or generates randomness - see reduce.ts for
 * why that matters.
 */

// ---------------------------------------------------------------------------
// Lot and ring lifecycle
// ---------------------------------------------------------------------------

/** Spec section 5. ONLINE_PAUSED is deliberately absent: it is a ring-level
 *  overlay, not a lot state, because a pause suspends internet participation
 *  without moving the lot through its lifecycle. */
export type LotState =
  | 'UPCOMING'
  | 'OPEN'
  | 'FAIR_WARNING'
  | 'HAMMERED_PENDING'
  | 'SOLD'
  | 'NO_SALE'
  | 'PASSED'
  | 'SCRATCHED';

export type OnlineStatus = 'OPEN' | 'PAUSED';

export type BidSource = 'ONLINE' | 'FLOOR' | 'PROXY';

/** Why online bidding is paused. (ADR-70 cut 3 removed `HEALTH`: nothing
 *  ever emitted it, and a reason no code can produce only misleads a reader
 *  of the type into thinking a health-driven pause exists.) */
export type PauseReason = 'CLERK' | 'FLOOR_INPUT_LOST';

/**
 * ADR-70 cut 3 (hitlist section 6): the auctioneer's close, step by step.
 * Set by SET_CLOSE_STEP; the lot reads FAIR_WARNING while a step stands, and
 * any accepted bid clears it and returns the lot to OPEN.
 */
export type CloseStep = 'GOING_ONCE' | 'GOING_TWICE' | 'LAST_CALL';
export const CLOSE_STEPS: readonly CloseStep[] = ['GOING_ONCE', 'GOING_TWICE', 'LAST_CALL'];
export const isCloseStep = (v: unknown): v is CloseStep => typeof v === 'string' && (CLOSE_STEPS as readonly string[]).includes(v);

/**
 * ADR-70 cut 3 (hitlist section 6): the ring's phase as an operator or a
 * bidder reads it, DERIVED from the lot state and the online overlay by
 * `ringPhase()`. Never stored: the lot state stays the one truth, and this is
 * its reading. "In the ring" is not "open for bidding", and a pause does not
 * take the lot out of the ring.
 */
export type RingPhase =
  | 'NOT_IN_RING'
  | 'IN_RING_WAITING_FOR_OPERATOR'
  | 'BIDDING_OPEN'
  | 'BIDDING_PAUSED'
  | 'CLOSING'
  | 'POSTSALE_PENDING'
  | 'SOLD'
  | 'PASSED'
  | 'REMOVED_FROM_RING';

export type PricingMode = 'PER_LOT' | 'PER_HEAD' | 'CHOICE';

/**
 * ADR-60 cut 5: how a GROUP of lots sells on one bid (brief section 21).
 *   SINGLE      one lot (the default; `sellingGroup` is null)
 *   ALL_TO_GO   one bid buys every lot in the group, as one price
 *   X_MONEY     the bid is per unit; the buyer pays bid x multiplier
 *   CHOICE      the winner picks one or more lots at the bid price each; the
 *               rest go back to the ring
 */
export type SellingMode = 'SINGLE' | 'ALL_TO_GO' | 'X_MONEY' | 'CHOICE';

/** The lots in the ring right now, when more than one sells on one bid. The
 *  lead lot is `currentLotId` (always `lotIds[0]`); the one bidding position
 *  is unchanged - a group is a fact about what the bid MEANS, not a second
 *  board. */
export interface SellingGroup {
  readonly lotIds: readonly string[];
  readonly mode: Exclude<SellingMode, 'SINGLE'>;
  /** X_MONEY: what the unit bid is multiplied by (defaults to the lot count). */
  readonly multiplier: number;
}

/** The settlement facts of a group sale, ON the LOT_SOLD (self-contained). */
export interface SoldGroup {
  readonly mode: Exclude<SellingMode, 'SINGLE'>;
  /** The lots that sold (CHOICE: the winner's selection). */
  readonly lotIds: readonly string[];
  /** The hammer per unit: the bid. */
  readonly unitPrice: number;
  /** ALL_TO_GO 1; X_MONEY the multiplier; CHOICE the number of lots taken. */
  readonly multiplier: number;
  /** unitPrice x multiplier, computed by the ring, never by a client. */
  readonly totalHammer: number;
  /** CHOICE: the lots not taken, which go back to UPCOMING. */
  readonly remainderLotIds: readonly string[];
}

export type ApprovalStatus = 'APPROVED' | 'SUSPENDED' | 'REVOKED' | 'PENDING';

export type ActorRole = 'clerk' | 'spotter' | 'bidder' | 'observer';

// ---------------------------------------------------------------------------
// Auction mode + policy - ADR-42 (one bid core, mode orchestration on top)
// ---------------------------------------------------------------------------

/**
 * How lots progress and close. The SHARED bid core (authorization, limits,
 * increments, at-ask guard, idempotency, outcomes, proxy resolution, settlement
 * fact) is identical across modes; mode decides the lifecycle:
 *   LIVE                 clerk-driven ring (OPEN → FAIR_WARNING → HAMMER → SOLD).
 *   TIMED                many lots open concurrently; each closes at an
 *                        authoritative scheduled time with a soft-close
 *                        extension policy; disposition is automatic.
 *   LIVE_WITH_PREBIDDING LIVE, plus max (proxy) bids accepted on lots before
 *                        they enter the ring; carried in at OPEN_LOT.
 */
export type AuctionMode = 'LIVE' | 'TIMED' | 'LIVE_WITH_PREBIDDING';

export interface ProxyPolicy {
  /** Whether bidders may place private max bids at all. */
  readonly enabled: boolean;
  /** CURRENT_LOT: only on the lot in the ring (LIVE). ANY_LOT: on any lot
   *  (TIMED, LIVE_WITH_PREBIDDING). */
  readonly scope: 'CURRENT_LOT' | 'ANY_LOT';
}

export interface PrebidPolicy {
  /** PROXY_AT_OPEN: when a pre-bid lot enters the ring, its active maxes run
   *  immediately from the opening ask and establish the opening high bid.
   *  NONE: maxes stay dormant until the first live bid. */
  readonly carryIn: 'PROXY_AT_OPEN' | 'NONE';
}

/** What happens to a lot's active max bids when a MATERIAL announcement is
 *  posted against it. Never silent - any invalidation is an event. */
export type MaterialAnnouncementPolicy = 'PRESERVE_MAX_BIDS' | 'INVALIDATE_MAX_BIDS' | 'REQUIRE_RECONFIRM';

export interface AnnouncementPolicy {
  readonly onMaterial: MaterialAnnouncementPolicy;
  /** ADR-60 cut 3: a MATERIAL announcement requires every bidder to
   *  acknowledge it before their next bid on that lot (or on any lot, when
   *  sale-wide), unless the command says otherwise. */
  readonly requireAckOnMaterial: boolean;
}

/** Soft-close policy for TIMED lots. Scheduling PATTERNS (individual /
 *  staggered / grouped) are a control-plane helper that computes each lot's
 *  concrete `closesAt` + `closeGroupId`; the reducer only ever sees those. */
export interface TimedClosePolicy {
  /** A bid accepted within this many ms of the scheduled close extends it. */
  readonly extensionWindowMs: number;
  /** How far the close moves when extended. */
  readonly extensionDurationMs: number;
  /** Cap on extensions per lot; null = unlimited. */
  readonly maxExtensions: number | null;
  /** TIMED_LOT_CLOSED → LOT_SOLD / LOT_NO_SALE in the same tick (true), or
   *  wait in CLOSED_PENDING for staff confirmation (false). */
  readonly autoDisposition: boolean;
  /** New close = bid time + duration (BID_TIME) or scheduled close + duration. */
  readonly extendFrom: 'BID_TIME' | 'SCHEDULED_CLOSE';
}

/**
 * ADR-60: operator-accepted online bids. When `RingState.confirmMode` is on,
 * an internet bid (a proxy step included) does not move the board; it waits
 * as the ring's ONE pending bid until the operator accepts it, the floor
 * supersedes it, the ask moves, the lot closes, or it expires.
 */
export interface ConfirmPolicy {
  /** How long a pending online bid waits for the operator before it expires
   *  (resolved EXPIRED by the tick, the bidder told). null = never. */
  readonly pendingTtlMs: number | null;
}

/**
 * ADR-70 cut 3 (hitlist section 7): how a LIVE ring moves between lots.
 *
 * `startLotsPaused`: a lot brought into the ring waits there
 * (IN_RING_WAITING_FOR_OPERATOR) until the operator opens bidding, instead
 * of the console opening it on arrival. The ring has always staged a lot
 * unbiddable (UPCOMING); this tells every console, and the advance
 * commands, not to open it for the operator.
 */
export interface LiveRingPolicy {
  readonly startLotsPaused: boolean;
}

/**
 * ADR-60: how many devices may hold a bidder's bidding lease.
 *   ONE_ACTIVE_BIDDING_DEVICE       one device may bid; others are view-only
 *   MULTIPLE_VIEW_ONLY_ONE_BIDDING  same fence, stated as the default posture
 *   MULTIPLE_ACTIVE_BIDDING_DEVICES no fence; every device may bid
 * The reducer fences with `BidderAuthorization.activeSessionId`; the socket
 * layer decides which device claims it (first HELLO, or an explicit take-over).
 */
export type SessionPolicy = 'ONE_ACTIVE_BIDDING_DEVICE' | 'MULTIPLE_VIEW_ONLY_ONE_BIDDING' | 'MULTIPLE_ACTIVE_BIDDING_DEVICES';

export interface SessionsPolicy {
  readonly policy: SessionPolicy;
}

/**
 * ADR-70 cut 2 (brief section 12): who wins when an online bid and a floor
 * bid land at the same amount. The first committed bid always takes the
 * board; the policy decides what may happen next.
 *   AUCTIONEER_AUTHORITY    the default: the auctioneer may award the equal
 *                           bid to the floor (AWARD_EQUAL_BID_ONSITE) and
 *                           undo that award (RESTORE_EQUAL_BID_ONLINE)
 *   FIRST_SERVER_TIMESTAMP  the first committed bid stands; no override
 *   ONSITE_PRIORITY         a floor bid at the online bid's amount takes the
 *                           bid automatically; the clerk may also award it
 *   ONLINE_PRIORITY         an online bid at the floor bid's amount takes
 *                           the bid automatically (not in confirm mode, where
 *                           the operator decides); the floor is never awarded
 *                           an online bid's amount
 * Every change of winner is an EQUAL_BID_OVERRIDDEN event: both bids stay
 * in the log, nothing is rewritten.
 */
export type EqualBidPolicy = 'AUCTIONEER_AUTHORITY' | 'FIRST_SERVER_TIMESTAMP' | 'ONSITE_PRIORITY' | 'ONLINE_PRIORITY';

export const EQUAL_BID_POLICIES: readonly EqualBidPolicy[] = ['AUCTIONEER_AUTHORITY', 'FIRST_SERVER_TIMESTAMP', 'ONSITE_PRIORITY', 'ONLINE_PRIORITY'];

export function isEqualBidPolicy(v: unknown): v is EqualBidPolicy {
  return typeof v === 'string' && (EQUAL_BID_POLICIES as readonly string[]).includes(v);
}

export interface EqualBidsPolicy {
  readonly policy: EqualBidPolicy;
}

export interface AuctionPolicy {
  readonly mode: AuctionMode;
  readonly live: LiveRingPolicy;
  readonly proxy: ProxyPolicy;
  readonly prebid: PrebidPolicy;
  readonly announcements: AnnouncementPolicy;
  readonly timed: TimedClosePolicy;
  readonly confirm: ConfirmPolicy;
  readonly sessions: SessionsPolicy;
  /** ADR-70 cut 2. */
  readonly equalBid: EqualBidsPolicy;
}

// ---------------------------------------------------------------------------
// Pending online bids - ADR-60 cut 2
// ---------------------------------------------------------------------------

/** How a pending online bid ended. Every one is an event, never silence. */
export type PendingResolution =
  | 'ACCEPTED'
  | 'REJECTED'
  | 'SUPERSEDED_BY_FLOOR'
  | 'ASK_CHANGED'
  | 'LOT_CLOSED'
  /** A material update requiring acknowledgement landed on the lot while the
   *  bid waited: what the bidder bid on has changed (ADR-60 cut 3). */
  | 'LOT_CHANGED'
  | 'EXPIRED';

// ---------------------------------------------------------------------------
// Material update acknowledgement - ADR-60 cut 3
// ---------------------------------------------------------------------------

/** An announcement bidders must acknowledge before bidding. Kept on the ring
 *  state (bounded: announcements are rare) so a personal snapshot can show
 *  the text without reading the log. */
export interface RequiredAcknowledgement {
  readonly seq: number;
  /** null = sale-wide (every lot). */
  readonly lotId: string | null;
  readonly text: string;
  readonly category: string;
  readonly severity: AnnouncementSeverity;
}

/** The scope key an acknowledgement is recorded under: a lot id, or '*' for
 *  sale-wide announcements. */
export type AckScope = string;
export const SALE_WIDE_SCOPE: AckScope = '*';

/** The ring's ONE pending online bid (confirm mode). Durable ring state: it
 *  survives a failover and a reconnect exactly like the board does. */
export interface PendingBid {
  readonly id: string;
  readonly bidderId: string;
  readonly sessionId: string;
  readonly lotId: string;
  readonly amount: number;
  /** ONLINE for a bidder's own intent; PROXY for a max stepping in. */
  readonly bidSource: BidSource;
  readonly proxyOfMaxSeq: number | null;
  /** The bidder's idempotency key (null for a proxy step), so a reconnecting
   *  client's re-sent key can be matched to the pending bid. */
  readonly idempotencyKey: string | null;
  readonly createdSeq: number;
  readonly createdAt: number;
  readonly expiresAt: number | null;
}

// ---------------------------------------------------------------------------
// Max (proxy) bids and per-lot positions - ADR-42
// ---------------------------------------------------------------------------

export type MaxBidStatus = 'ACTIVE' | 'CANCELLED' | 'EXHAUSTED' | 'INVALIDATED';

/** A bidder's private maximum on one lot. The amount NEVER reaches the public
 *  channel and is redacted from the floor channel too (the auctioneer must not
 *  see it). Proxy executions are ordinary BID_ACCEPTED events with
 *  bidSource PROXY that point back at `placedSeq`. */
export interface MaxBid {
  readonly bidderId: string;
  readonly lotId: string;
  readonly maxAmount: number;
  /** Seq of the MAX_BID_PLACED that established this amount - the tie-break
   *  (earlier wins) and the provenance anchor. Raising a max re-stamps it. */
  readonly placedSeq: number;
  readonly sessionId: string;
  readonly status: MaxBidStatus;
  /** Why it stopped being ACTIVE (announcement seq, revocation, ...). */
  readonly endedReason?: string;
}

/**
 * ADR-70 cut 6e (hitlist 9.2): what an operator may see of the max bids on a
 * lot once a Super Admin has switched operator max-bid visibility on for the
 * tenant. The hitlist's sub-permission names. Which role holds which is
 * MAX_BID_VIEW_SCOPES (permissions.ts, Q13).
 */
export const MAX_BID_VIEW_SCOPE_NAMES = ['view_proxy_exists', 'view_proxy_bidder', 'view_proxy_amount', 'view_proxy_remaining_capacity'] as const;
export type MaxBidViewScope = (typeof MAX_BID_VIEW_SCOPE_NAMES)[number];

/** The staff role behind a floor seat, as the ring proved it at HELLO. A
 *  tenant admin and a tenant clerk both sit at the console as the ring's
 *  clerk; only this tells them apart. */
export type MaxBidViewerRole = 'admin' | 'clerk';

/** One active max as an operator was shown it (MAX_BID_VIEWED). Each field
 *  past `placedSeq` is present only when the viewer's role may see it. */
export interface ViewedMaxBid {
  readonly placedSeq: number;
  readonly bidderId?: string;
  readonly maxAmount?: number;
  /** How far above the standing bid the max can still go; null with no bid. */
  readonly remaining?: number | null;
}

/** ADR-70 cut 6e: the tenant's live-bidding capabilities as the control
 *  plane last sent them (hitlist section 33). The ring holds the newest. */
export interface RingCapabilities {
  readonly version: number;
  /** live_bidding.operator_max_bid_visibility. */
  readonly operatorMaxBidVisibility: boolean;
  /** ADR-70 cut 6f: live_bidding.operator_intent_signals. Absent (a ring
   *  that last heard a 6e message): off. */
  readonly operatorIntentSignals?: boolean;
  /** ADR-70 cut 6f: what the switch shows the operator (hitlist 10.3).
   *  Absent: everything off. */
  readonly intentControls?: IntentControls;
}

/** ADR-70 cut 6f (hitlist 10.3): the granular controls under operator intent
 *  signals, each one a part of what a bidder's page tells the ring console.
 *  The ring collects only what a control shows. */
export interface IntentControls {
  /** How many people are watching this ring (an anonymous count). */
  readonly showWatchers: boolean;
  /** Which approved bidders have the sale open, and how many. */
  readonly showBidderPresence: boolean;
  /** A bidder's pointer resting on the Bid button of the lot in the ring. */
  readonly showHoverIntent: boolean;
  /** A bidder's bid confirmation (or outbid prompt) is open. */
  readonly showConfirmIntent: boolean;
  /** A bidder just pressed Bid (the ring sees the bid arrive). */
  readonly showBidSubmitted: boolean;
  /** Name the bidder (paddle) instead of "Online bidder 3". */
  readonly showBidderIdentity: boolean;
}

/** ADR-70 cut 6f: the controls in the hitlist's order. */
export const INTENT_CONTROL_KEYS = [
  'showWatchers', 'showBidderPresence', 'showHoverIntent', 'showConfirmIntent', 'showBidSubmitted', 'showBidderIdentity',
] as const satisfies readonly (keyof IntentControls)[];

/** The bidding position the shared bid core mutates - the same fields the
 *  LIVE ring keeps at top level, for lots that bid outside the ring (TIMED,
 *  pre-bidding). */
export interface LotPosition {
  readonly currentHighBid: number | null;
  readonly currentHighBidder: string | null;
  readonly currentHighBidSource: BidSource | null;
  readonly currentHighPaddle: string | null;
  readonly currentHighBidSeq: number | null;
  readonly currentAsk: number | null;
  readonly lastBid: ReversibleBid | null;
}

/** ADR-65: VOIDED = the lot's sale was reversed at the ring; OPEN_TIMED_LOT
 *  accepts a VOIDED lot with a fresh schedule (a second life, new openedSeq). */
export type TimedLotPhase = 'OPEN' | 'EXTENDED' | 'CLOSED_PENDING' | 'SOLD' | 'NO_SALE' | 'VOIDED';

/** A TIMED lot: a position plus its authoritative close schedule. Every change
 *  to `scheduledCloseAt` is also an event (LOT_CLOSE_EXTENDED), so the history
 *  is provable from the log, not just from this mutable field. */
export interface TimedLot extends LotPosition {
  readonly lotId: string;
  readonly phase: TimedLotPhase;
  readonly openedSeq: number;
  readonly originalCloseAt: number;
  readonly scheduledCloseAt: number;
  readonly extensions: number;
  readonly pricingMode: PricingMode;
  /** Reserve in whole dollars; null = none. Close below reserve → NO_SALE. */
  readonly reserve: number | null;
  /** Lots sharing a group extend together (cascading close). */
  readonly closeGroupId: string | null;
  /** Seq of the TIMED_LOT_CLOSED event once closed. */
  readonly closeSeq: number | null;
}

// ---------------------------------------------------------------------------
// Ring state - spec section 4
// ---------------------------------------------------------------------------

export interface BidderAuthorization {
  readonly approvalStatus: ApprovalStatus;
  readonly approvalVersion: number;
  /** Tenant-set ceiling on this bidder's bids. Absent means no limit. */
  readonly bidLimit?: number;
  /** The device holding the single active bidding session (spec 8.14). */
  readonly activeSessionId?: string;
}

/**
 * Everything needed to reverse the most recent bid.
 *
 * Held as explicit undo information rather than recomputed, because a
 * correction must restore exactly the prior state - including an ask the clerk
 * had overridden off the increment ladder, which cannot be derived from the
 * bid amount alone.
 *
 * Only the most recent bid is reversible on the fast path (spec 8.10); anything
 * older has dependent bids and requires reconciliation.
 */
export interface ReversibleBid {
  readonly seq: number;
  readonly source: BidSource;
  readonly bidderId: string;
  readonly amount: number;
  readonly previousHighBid: number | null;
  readonly previousHighBidder: string | null;
  readonly previousHighBidSource: BidSource | null;
  readonly previousHighPaddle: string | null;
  readonly previousHighBidSeq: number | null;
  readonly previousAsk: number | null;
}

/** ADR-64: how a lot left the ring. The three terminal dispositions that are
 *  events in the log (SCRATCHED is a catalog fact, never a ring event).
 *  ADR-65: VOIDED, when the ring's last word on a lot is that its sale no
 *  longer stands (SALE_VOIDED). */
export type LotOutcome = 'SOLD' | 'NO_SALE' | 'PASSED' | 'VOIDED';

/** ADR-64: the ring's last disposition, as a fact on the state (see
 *  RingState.previousLot). Public by construction: everything here is already
 *  on the public wire as the disposition event itself. */
export interface PreviousLot {
  readonly lotId: string;
  readonly outcome: LotOutcome;
  /** SOLD: the LOT_SOLD price (the unit price for a group). NO_SALE and
   *  PASSED: the standing high bid when the lot left, or null if none. */
  readonly price: number | null;
  /** Seq of the disposition event. */
  readonly seq: number;
  /** Engine time of the disposition (the event's serverTs). */
  readonly at: number;
}

/** ADR-70 cut 2: an equal bid the clerk (or the policy) awarded to the floor,
 *  kept while its floor bid is the standing bid so RESTORE_EQUAL_BID_ONLINE
 *  can put the online bidder back exactly. Private: it names the bidder. */
export interface EqualBidAward {
  /** The EQUAL_BID_OVERRIDDEN that made the award. */
  readonly overrideSeq: number;
  readonly lotId: string;
  /** The floor BID_ACCEPTED the award placed. */
  readonly floorBidSeq: number;
  /** The online (or proxy) bid the award displaced. */
  readonly displacedBidSeq: number;
  readonly displacedBidderId: string;
  readonly displacedBidSource: BidSource;
}

/**
 * ADR-70 cut 6c (hitlist 17.3): the lot that most recently left the ring
 * passed or no-sale, kept so REOPEN_LOT can bring it back exactly as it left:
 * its board, its group, its required announcements and the ring's previous
 * lot before it. Private: the board names the high bidder. Replaced by the
 * next pass or no-sale, cleared by any sale, by a re-offer of the lot and by
 * the reopen itself.
 */
export interface DepartedLot {
  readonly lotId: string;
  readonly outcome: 'PASSED' | 'NO_SALE';
  /** Seq of the LOT_PASSED or LOT_NO_SALE. */
  readonly seq: number;
  readonly position: LotPosition;
  readonly sellingGroup: SellingGroup | null;
  /** The lot's required announcements the departure dropped, or null. */
  readonly requiredAcks: readonly RequiredAcknowledgement[] | null;
  /** RingState.previousLot before the departure replaced it. */
  readonly previousLot: PreviousLot | null;
  /** ADR-70 cut 6d: the increment the clerk chose for the lot, so a reopen
   *  brings it back too. Absent on blobs written before 6d (= none). */
  readonly incrementStep?: number | null;
}

/**
 * ADR-70 cut 6d (hitlist 14, "choose increment"): the step the clerk chose for
 * the lot in the ring, in place of the sale's increment table. It holds for
 * that lot only: a new lot in the ring starts on the table again.
 */
export interface ChosenIncrement {
  readonly lotId: string;
  /** Whole dollars, at least 1. */
  readonly step: number;
}

/** ADR-65: one sale the ring made, as the ring remembers it (RingState.sales). */
export interface RingSale {
  /** The LOT_SOLD that stands, or stood. */
  readonly soldSeq: number;
  /** The LOT_SOLD price (the unit price for a group). */
  readonly price: number;
  readonly bidSource: BidSource;
  /** Private: the raw high-bidder id the LOT_SOLD carried. */
  readonly buyerId: string;
  /** Private: the winning paddle for a floor sale, null online. */
  readonly paddle: string | null;
  /** Every lot of the group sale this entry belongs to, or null for a single. */
  readonly groupLotIds: readonly string[] | null;
  /** The SALE_VOIDED that reversed this sale, or null while it stands. */
  readonly voidedSeq: number | null;
}

export interface RingState {
  readonly tenantId: string;
  readonly saleId: string;
  readonly ringId: string;

  readonly engineEpoch: number;
  readonly lastSeq: number;
  readonly nextSeq: number;

  /** Settlement fence (§ close). Null while the sale is running. Once set (by
   *  FINALIZE_SALE) the ring accepts NO further commands, so the SALE_FINALIZED
   *  event at this seq is provably the last event - its seq is the authoritative
   *  final sequence a settlement can be sealed through. This is what lets close
   *  trust the log as complete; an empty read from a still-running ring is not. */
  readonly finalizedSeq: number | null;

  /** Clerk-controlled. Spec 4.1 - bidders can never change this. */
  readonly currentLotId: string | null;
  readonly lotState: LotState;
  /** ADR-70 cut 3: the close step called on the lot in the ring, or null.
   *  Non-null only while the lot is FAIR_WARNING. */
  readonly closeStep: CloseStep | null;

  readonly currentHighBid: number | null;
  readonly currentHighBidder: string | null;
  readonly currentHighBidSource: BidSource | null;
  /** Paddle of the current high bid when it came from the floor (§11.2: the
   *  clerk's primary key for a human). Null for online bids. Floor-channel
   *  only - never in the public snapshot. */
  readonly currentHighPaddle: string | null;
  /** Sequence of the accepted bid that set the current high bid - so LOT_SOLD
   *  can carry the winning bid's seq as a self-contained settlement fact (M6),
   *  without the projector correlating back through history. */
  readonly currentHighBidSeq: number | null;
  readonly currentAsk: number | null;

  readonly onlineStatus: OnlineStatus;
  readonly pauseReason: PauseReason | null;
  readonly confirmMode: boolean;

  /** Epoch millis of the most recent floor-input heartbeat. Drives the
   *  auto-pause rule in spec 9.1. Null means none ever received. */
  readonly floorInputHealthyAt: number | null;

  /** Undo information for the most recent accepted bid, or null if no bid has
   *  been accepted on the current lot. Cleared when a lot opens. */
  readonly lastBid: ReversibleBid | null;

  readonly incrementTableId: string;
  readonly bidders: Readonly<Record<string, BidderAuthorization>>;

  /** ADR-60: the one pending online bid awaiting the operator (confirm mode),
   *  or null. Its amount always equals `currentAsk` while it stands - every
   *  ask move resolves it first. */
  readonly pendingBid: PendingBid | null;

  /** ADR-60 cut 5: the lots selling on the one bid right now, when more than
   *  one. null = a single lot (`currentLotId`). Cleared with the lot. */
  readonly sellingGroup: SellingGroup | null;

  /** ADR-70 cut 2: the last equal-bid award to the floor, or null. Only
   *  honoured while its floor bid is still the standing bid on its lot.
   *  null on a fresh ring and on blobs written before it existed. */
  readonly equalBidAward: EqualBidAward | null;

  /**
   * ADR-64: the ring's most recent disposition, so a client that joins
   * between lots can show "Hip 41 sold $18,500" before the next lot opens.
   * Set by the reducer when LOT_SOLD, LOT_NO_SALE or LOT_PASSED commits
   * (live and timed paths); never cleared by SET_CURRENT_LOT. Derived from
   * events that already exist, so replay reproduces it and no new event type
   * is needed. null on a fresh ring and on durable blobs written before it
   * existed (normalizeRingState fills the hole).
   */
  readonly previousLot: PreviousLot | null;

  /** ADR-70 cut 6c: the last lot that left passed or no-sale, while it may
   *  still be reopened (see DepartedLot). null on a fresh ring and on blobs
   *  written before it existed. */
  readonly lastDeparted: DepartedLot | null;

  /** ADR-70 cut 6d: the increment the clerk chose (see ChosenIncrement). It
   *  applies only while its lot is the lot in the ring; SET_CURRENT_LOT
   *  clears it. null = the sale's table; null on blobs written before it. */
  readonly chosenIncrement: ChosenIncrement | null;

  /**
   * ADR-65: the sales this ring has made, by lot, so the ring can refuse to
   * sell a lot a second time while its first sale stands (LOT_ALREADY_SOLD)
   * and can void a sale it knows about (VOID_SALE). One entry per lot a
   * LOT_SOLD named (a group writes one per member, all at the one soldSeq);
   * a resale after a void replaces the entry, the earlier life staying in the
   * log and the ledger. Persisted per item (SALE#), like approvals, maxes and
   * acknowledgements, so a long sale never bloats the STATE blob. Empty on a
   * ring resumed from a blob older than this field: the guard then covers
   * sales made after the upgrade, which is the truth.
   */
  readonly sales: Readonly<Record<string, RingSale>>;

  /** ADR-60 cut 3: announcements that require acknowledgement, by scope (lot
   *  id or '*'). Bounded; part of the STATE blob. */
  readonly requiredAcks: Readonly<Record<AckScope, readonly RequiredAcknowledgement[]>>;
  /** ADR-60 cut 3: per bidder, per scope, the highest announcement seq they
   *  have acknowledged. Persisted per item (ACK#), like approvals and maxes,
   *  so a large crowd never bloats the STATE blob. */
  readonly acknowledgements: Readonly<Record<string, Readonly<Record<AckScope, number>>>>;

  // ---- ADR-42: mode orchestration + shared bid core extensions ----
  /** Fixed at ring creation; persisted with the state; shown in snapshots. */
  readonly policy: AuctionPolicy;
  /** The provisioning generation this ring was created for (ADR-48). Fixed at
   *  creation, persisted, and reported in snapshots so control-api can confirm
   *  the running engine belongs to the CURRENT provisioning - a stale engine
   *  from an old generation carries the old id and fails the go-live check.
   *  null on pre-ADR-48 rings. */
  readonly provisioningId: string | null;
  /** Lots that bid OUTSIDE the single live position: every TIMED lot, and
   *  pre-bid lots before they enter the ring. Keyed by lotId. Empty in LIVE. */
  readonly lots: Readonly<Record<string, TimedLot>>;
  /** Private max bids: lotId → bidderId → MaxBid. Persisted per item (like
   *  bidder authorizations) so the state blob stays bounded. */
  readonly maxBids: Readonly<Record<string, Readonly<Record<string, MaxBid>>>>;

  /** ADR-70 cut 5f: which rings of this sale each bound floor staffer may
   *  run, as the control plane last sent it. null = no roster received, so
   *  every floor staffer of the tenant is unbound (every ring), as before. */
  readonly ringStaff: RingStaffRoster | null;

  /** ADR-70 cut 6e: the tenant's live-bidding capabilities as the control
   *  plane last sent them. null = none received, so every capability is off
   *  (operator max-bid visibility is OFF by default, hitlist 9.2). */
  readonly capabilities: RingCapabilities | null;
}

/**
 * ADR-70 cut 5f: the sale's ring staff roster, whole, at one version. A
 * staffer with an entry may run only the rings listed; a staffer with no
 * entry is unbound. The ring checks it at HELLO (`ringStaffAllows`); it never
 * decides a bid.
 */
export interface RingStaffRoster {
  /** The control plane's counter for this sale's roster; only a newer one
   *  replaces it (redelivery and reordering are harmless). */
  readonly version: number;
  /** Staff actor id (the Cognito username) -> the engine ring ids they may run. */
  readonly assignments: Readonly<Record<string, readonly string[]>>;
}

// ---------------------------------------------------------------------------
// Commands - spec section 6
// ---------------------------------------------------------------------------

export interface Actor {
  readonly id: string;
  readonly role: ActorRole;
}

/**
 * ADR-70 cut 6b (hitlist section 8.2): where a command came from, in the
 * hitlist's words. The device class is said separately, so a bidder page on
 * a phone is WEB_LIVE + MOBILE and MOBILE_WEB is never needed for it; the
 * value stays for a client that is a mobile site and nothing else.
 */
export const BID_ORIGINS = [
  'WEB_DETAIL', 'WEB_CATALOG', 'WEB_LIVE', 'MOBILE_WEB', 'MOBILE_APP', 'OPERATOR', 'RING_CLERK',
  'ONSITE', 'PHONE', 'ABSENTEE', 'MAX_BID', 'API', 'AI_AGENT', 'IMPORT',
] as const;
export type BidOrigin = (typeof BID_ORIGINS)[number];

/** ADR-70 cut 6b: the class of device a command came from, read by the ring
 *  from the connection (never the client's word). UNKNOWN: it did not say. */
export const DEVICE_CLASSES = ['DESKTOP', 'TABLET', 'MOBILE', 'UNKNOWN'] as const;
export type DeviceClass = (typeof DEVICE_CLASSES)[number];

/** ADR-70 cut 6b: the connection a command arrived on, as the ring server
 *  decided it. `origin` null: the client did not say where it is.
 *  `viewport`: the bidder's window at the bid, in CSS pixels, as the client
 *  reported it - evidence on the bid attempt only, never on an event, and
 *  never used to decide anything. */
export interface CommandOrigin {
  readonly origin: BidOrigin | null;
  readonly deviceClass: DeviceClass;
  readonly viewport?: { readonly w: number; readonly h: number };
}

interface CommandBase {
  readonly actor: Actor;
  /** Server receive time in epoch millis, injected. The reducer never reads a
   *  clock itself. */
  readonly now: number;
  /**
   * ADR-70 cut 6b: the connection this command arrived on, set by the ring
   * server from the socket and never taken from the frame. The engine stamps
   * it on the events the command's own actor made (audit.ts `auditOrigin`);
   * the reducer never reads it.
   */
  readonly origin?: CommandOrigin;
  /**
   * ADR-70: the operator device's id for this one action, minted once and
   * reused on every retry. The engine keeps one immutable record per
   * (actor, id) and answers a repeat with it, so a reconnect can never apply
   * the action twice. Bids keep their own `idempotencyKey`; the reducer never
   * reads this.
   */
  readonly clientActionId?: string;
}

/**
 * ADR-70: what the operator's screen showed when they pressed the button.
 * The ring `seq` is the stream version and moves on unrelated events
 * (registrations, maxes on other lots), so a destructive command pins the
 * decision instead: the lot in the ring, the seq of the standing high bid,
 * and, when given, the held internet bid and the ask. Any difference is
 * STALE_AUCTION_STATE: the command is refused and the console reloads.
 */
export interface ExpectedRingView {
  readonly lotId: string | null;
  /** `currentHighBidSeq`: null while the lot has no bid. */
  readonly highBidSeq: number | null;
  /** The held internet bid's id (null: none shown). Omitted: not checked. */
  readonly pendingBidId?: string | null;
  /** The ask the command was computed from. Omitted: not checked. */
  readonly ask?: number | null;
}

/** Commands that act on the lot in the ring and may carry `expected`. */
export const STALE_GUARDED_COMMANDS = [
  'SET_CURRENT_LOT',
  'SET_ASK',
  'DECLARE_FAIR_WARNING',
  'SET_CLOSE_STEP',
  'SELL_AND_ADVANCE',
  'PASS_AND_ADVANCE',
  'HAMMER',
  'CONFIRM_SOLD',
  'DECLARE_NO_SALE',
  'DECLARE_PASSED',
  'REOPEN_LOT',
  'AWARD_EQUAL_BID_ONSITE',
  'RESTORE_EQUAL_BID_ONLINE',
  'RETRACT_LAST_BID',
  'RESET_BIDDING',
  'SET_INCREMENT',
  'REMOVE_FROM_RING',
] as const;
export type StaleGuardedCommandType = (typeof STALE_GUARDED_COMMANDS)[number];

export type Command = CommandBase &
  (
    | { readonly type: 'SET_CURRENT_LOT'; readonly lotId: string; readonly expected?: ExpectedRingView }
    | {
        readonly type: 'OPEN_LOT';
        readonly lotId: string;
        readonly openingAsk: number;
        /**
         * ADR-60 cut 5: sell this lot together with others on one bid. `lotId`
         * must be the first of `lotIds` (the lead the board names); every id
         * distinct. X_MONEY defaults its multiplier to the lot count.
         */
        readonly group?: { readonly lotIds: readonly string[]; readonly mode: Exclude<SellingMode, 'SINGLE'>; readonly multiplier?: number };
      }
    | {
        readonly type: 'PLACE_BID';
        readonly lotId: string;
        readonly atAsk: number;
        readonly idempotencyKey: string;
        readonly sessionId: string;
      }
    | {
        readonly type: 'PLACE_FLOOR_BID';
        readonly lotId: string;
        readonly atAsk: number;
        readonly idempotencyKey: string;
        readonly paddleNumber?: string;
        /**
         * A CUT bid: the auctioneer takes a floor bid at an amount other than
         * the standing ask ("five seventy-five"). With `cutAsk`, the reducer
         * moves the ask to `atAsk` AND accepts the bid in ONE atomic transition
         * (ASK_SET then BID_ACCEPTED, consecutive seqs, one commit) - so no
         * competing online bid can occupy the cut ask in between. Without it,
         * a floor bid off the ask is rejected like any other (ASK_CHANGED /
         * OUTBID_IN_FLIGHT). Clerk-only: a cut moves the ask, which is clerk
         * authority (spotters enter floor bids at the ask only).
         */
        readonly cutAsk?: boolean;
      }
    | { readonly type: 'SET_ASK'; readonly lotId: string; readonly ask: number; readonly reason?: string; readonly expected?: ExpectedRingView }
    | { readonly type: 'DECLARE_FAIR_WARNING'; readonly lotId: string; readonly expected?: ExpectedRingView }
    /**
     * ADR-70 cut 3: call a close step on the lot in the ring ("going once",
     * "going twice", "last call"), or `null` to take it back to open bidding.
     * The lot reads FAIR_WARNING while a step stands. Any step may follow any
     * other (an auctioneer may go straight to last call); repeating the step
     * already standing is refused.
     */
    | { readonly type: 'SET_CLOSE_STEP'; readonly lotId: string; readonly step: CloseStep | null; readonly expected?: ExpectedRingView }
    | { readonly type: 'HAMMER'; readonly lotId: string; readonly expected?: ExpectedRingView }
    | {
        readonly type: 'CONFIRM_SOLD';
        readonly lotId: string;
        readonly pricingMode: PricingMode;
        /**
         * "Number, please?" - the winning bidder's paddle, collected AFTER the
         * hammer falls.
         *
         * A clerk taking bids off the floor does not know who is bidding. They
         * are recording an amount going up while the auctioneer chants; the
         * bidder is a raised hand at the back. Identity is asked for exactly
         * once, of exactly one person: whoever is still holding their hand up
         * when the hammer drops. That is what HAMMERED_PENDING is for.
         *
         * Only consulted when the winning bid came from the FLOOR and carried
         * no paddle of its own. An online winner is already identified by
         * their account, and a floor bid whose paddle the clerk already knew
         * keeps the paddle it was placed with - a value here never overwrites
         * a bid that was attributed when it was taken.
         */
        readonly winningPaddleNumber?: string;
        /** ADR-60 cut 5: for a CHOICE group, the lots the winner took (one or
         *  more of the group). Required for CHOICE, ignored otherwise. */
        readonly selectedLotIds?: readonly string[];
        readonly expected?: ExpectedRingView;
      }
    | { readonly type: 'DECLARE_NO_SALE'; readonly lotId: string; readonly expected?: ExpectedRingView }
    | { readonly type: 'DECLARE_PASSED'; readonly lotId: string; readonly expected?: ExpectedRingView }
    /**
     * ADR-70 cut 3 (hitlist section 13): CONFIRM_SOLD on the hammered lot and
     * SET_CURRENT_LOT to `nextLotId`, as ONE decision: both commit or neither
     * does, and no other command lands between them. The next lot is staged,
     * never opened (opening is the operator's, or the console's when the ring
     * does not start lots paused).
     */
    | {
        readonly type: 'SELL_AND_ADVANCE';
        readonly lotId: string;
        readonly pricingMode: PricingMode;
        readonly winningPaddleNumber?: string;
        readonly selectedLotIds?: readonly string[];
        readonly nextLotId: string;
        readonly expected?: ExpectedRingView;
      }
    /** ADR-70 cut 3: DECLARE_PASSED and SET_CURRENT_LOT to `nextLotId`, as one
     *  decision (see SELL_AND_ADVANCE). */
    | { readonly type: 'PASS_AND_ADVANCE'; readonly lotId: string; readonly nextLotId: string; readonly expected?: ExpectedRingView }
    /**
     * Back to open bidding. From HAMMERED_PENDING (the lot in the ring), or
     * (ADR-70 cut 6c, hitlist 17.3) a lot that passed or no-saled: the lot in
     * the ring, or the last one that left while the lot now in the ring holds
     * no bid. Its board comes back as it left. A reason is required on the
     * cut 6c paths.
     */
    | { readonly type: 'REOPEN_LOT'; readonly lotId: string; readonly reason: string; readonly expected?: ExpectedRingView }
    | { readonly type: 'CORRECT_FLOOR_BID'; readonly reversesSeq: number; readonly reason: string }
    /**
     * ADR-70 cut 6c (hitlist 17.1): take back the standing bid whatever its
     * source - floor, online or a max. The bid stays in the log; BID_RETRACTED
     * is the compensating fact and the board goes back to what it was before
     * the bid. Only the tip (`reversesSeq` must be the last bid) and only
     * while bidding. An online or max bid also ends that bidder's active max
     * on the lot, or it would place the same bid again at once. Clerk only;
     * a reason is required.
     */
    | { readonly type: 'RETRACT_LAST_BID'; readonly lotId: string; readonly reversesSeq: number; readonly reason: string; readonly expected?: ExpectedRingView }
    /**
     * ADR-70 cut 6c (hitlist 17.2): clear the board and restart the lot's
     * bidding at `ask`. Every bid stays in the log; BIDDING_RESET marks them
     * all as reset. With `clearMaxBids` every active max on the lot ends too
     * (each its own MAX_BID_INVALIDATED); without it they bid again from the
     * restart ask. Only while bidding (after the hammer, reopen first). Clerk
     * only; a reason is required.
     */
    | { readonly type: 'RESET_BIDDING'; readonly lotId: string; readonly ask: number; readonly clearMaxBids: boolean; readonly reason: string; readonly expected?: ExpectedRingView }
    /**
     * ADR-70 cut 6d (hitlist 14, "choose increment"): bid up the lot in the
     * ring by `step` dollars instead of the sale's increment table, or go back
     * to the table with null. With a bid on the board the ask moves to the
     * next step above it at once; with none the opening ask stands. Max bids
     * step by it too. For that lot only. Clerk only; LIVE only.
     */
    | { readonly type: 'SET_INCREMENT'; readonly lotId: string; readonly step: number | null; readonly expected?: ExpectedRingView }
    /**
     * ADR-70 cut 6d (hitlist 14, "remove from ring"): the lot in the ring
     * leaves it with no result and stays in the sale, to come back later; the
     * ring has no lot until the clerk brings one in. Only while the lot holds
     * no bid (LOT_IN_PROGRESS otherwise: sell, pass or reset first). The
     * reason is optional and stays on the floor. Clerk only; LIVE only.
     */
    | { readonly type: 'REMOVE_FROM_RING'; readonly lotId: string; readonly reason?: string; readonly expected?: ExpectedRingView }
    /**
     * ADR-70 cut 2 (brief section 12): the auctioneer had the floor first at
     * the amount the standing online bid holds. The floor takes the bid at
     * that same amount (a FLOOR BID_ACCEPTED, the ask unchanged); the online
     * bid stays in the log, and EQUAL_BID_OVERRIDDEN records who lost it,
     * who won it, who decided and why. `bidSeq` names the online bid the
     * screen showed: any other standing bid is STALE_AUCTION_STATE. A max
     * behind the displaced bid keeps working (it bids the next step at
     * once). Clerk only; refused unless the ring's equal-bid policy is
     * AUCTIONEER_AUTHORITY or ONSITE_PRIORITY.
     */
    | {
        readonly type: 'AWARD_EQUAL_BID_ONSITE';
        readonly lotId: string;
        readonly bidSeq: number;
        readonly reason: string;
        /** The floor bidder's paddle when the clerk has it; otherwise asked for at SOLD as usual. */
        readonly paddleNumber?: string;
        readonly expected?: ExpectedRingView;
      }
    /**
     * ADR-70 cut 2: undo the award `overrideSeq` made, while its floor bid is
     * still the standing bid: the online bid it displaced holds the bid
     * again. A second EQUAL_BID_OVERRIDDEN, never an edit. Clerk only;
     * AUCTIONEER_AUTHORITY only.
     */
    | { readonly type: 'RESTORE_EQUAL_BID_ONLINE'; readonly lotId: string; readonly overrideSeq: number; readonly reason: string; readonly expected?: ExpectedRingView }
    | { readonly type: 'PAUSE_ONLINE' }
    | { readonly type: 'RESUME_ONLINE' }
    | { readonly type: 'HEARTBEAT' }
    // ---- ADR-60: operator-accepted online bids ----
    /** The operator takes the pending internet bid: it becomes an ordinary
     *  BID_ACCEPTED through the shared bid core. Floor-input roles. */
    | { readonly type: 'ACCEPT_ONLINE_BID'; readonly pendingBidId: string }
    /** The operator refuses it. A declined proxy step also ends that max (it
     *  would only re-pend), as its own PROXY_EXHAUSTED{DECLINED}. */
    | { readonly type: 'DECLINE_ONLINE_BID'; readonly pendingBidId: string; readonly reason?: string }
    /** Switch confirm mode. Refused while a lot is live; once the sale has
     *  started (a lot has been in the ring) a reason is required - a recorded
     *  operational decision, never a silent flip. Clerk only. */
    | { readonly type: 'SET_CONFIRM_MODE'; readonly enabled: boolean; readonly reason?: string }
    // ---- ADR-60: bidder session fencing (spec 8.14) ----
    /** This device takes the bidder's bidding lease. Other devices of the
     *  same bidder become view-only (their next bid is SESSION_NOT_ACTIVE)
     *  and are told by the private event. A no-op under
     *  MULTIPLE_ACTIVE_BIDDING_DEVICES. */
    | { readonly type: 'CLAIM_BIDDING_SESSION'; readonly sessionId: string }
    | {
        readonly type: 'SET_BIDDER_APPROVAL';
        readonly bidderId: string;
        readonly approvalStatus: ApprovalStatus;
        readonly approvalVersion: number;
        readonly bidLimit?: number;
      }
    /** ADR-70 cut 5f: the sale's ring staff roster from the control plane.
     *  Control plane only: no socket role holds ring.set_ring_staff, and the
     *  reducer accepts it only from CONTROL_PLANE_ACTOR_ID. */
    | {
        readonly type: 'SET_RING_STAFF';
        readonly version: number;
        readonly assignments: Readonly<Record<string, readonly string[]>>;
      }
    /** ADR-70 cut 6e: the tenant's live-bidding capabilities from the control
     *  plane, whole, at a version. Control plane only, like SET_RING_STAFF. */
    | {
        readonly type: 'SET_RING_CAPABILITIES';
        readonly version: number;
        readonly operatorMaxBidVisibility: boolean;
        /** ADR-70 cut 6f: absent from a 6e control plane (off). */
        readonly operatorIntentSignals?: boolean;
        readonly intentControls?: IntentControls;
      }
    /**
     * ADR-70 cut 6e (hitlist 9.2): an operator asks to see the max bids
     * standing on a lot. Recorded (MAX_BID_VIEWED) every time it is accepted.
     * `viewerRole` is the staff role the ring proved at HELLO; the socket
     * layer sets it and ignores any value the console sends.
     */
    | { readonly type: 'VIEW_MAX_BIDS'; readonly lotId: string; readonly viewerRole?: MaxBidViewerRole }
    /** Time-driven evaluation. The engine issues this on a timer so that the
     *  floor-input auto-pause (spec 9.1) stays a pure function of state and an
     *  injected clock, and is therefore testable without waiting. */
    | { readonly type: 'TICK' }
    /** Seal the ring for settlement. After this, EVERY command is rejected, so
     *  the emitted SALE_FINALIZED is the last event and its seq is the final
     *  sequence. The fence a truthful close depends on. */
    | { readonly type: 'FINALIZE_SALE' }
    /**
     * ADR-65: reverse a sale this ring made. Clerk only, reason required. The
     * sale is named by lot and by the LOT_SOLD seq so a stale console can
     * never void a resale it has not seen. `kind` is the ledger's word for
     * why (VOID = clerical, TURN_BACK = contractual); the catalog and the
     * public wire never learn it (R11). Refused after FINALIZE_SALE like every
     * command: after the fence the office door is the only door (R7).
     */
    | {
        readonly type: 'VOID_SALE';
        readonly lotId: string;
        readonly soldSeq: number;
        readonly kind: SaleReversalKind;
        readonly reason: string;
      }
    // ---- ADR-42: max (proxy) bids ----
    /**
     * A bidder's PRIVATE maximum on a lot. The engine bids on their behalf, at
     * the ask, one increment at a time, only as far as needed - through the same
     * commit path as every other bid (BID_ACCEPTED, bidSource PROXY). Placing a
     * max on a lot that is open and bidding executes immediately if eligible.
     * Re-placing replaces (raise any time; lowering never below the bidder's own
     * standing high bid) and re-stamps tie priority. Idempotent like PLACE_BID.
     */
    | {
        readonly type: 'PLACE_MAX_BID';
        readonly lotId: string;
        readonly maxAmount: number;
        readonly idempotencyKey: string;
        readonly sessionId: string;
      }
    /** Withdraw a standing max. Bids already placed by it stand (a standing
     *  high bid is never retracted by cancelling the max). */
    | { readonly type: 'CANCEL_MAX_BID'; readonly lotId: string; readonly idempotencyKey: string }
    /** Clerk/policy action: invalidate every active max on a lot (e.g. after a
     *  material change). Each max gets its own MAX_BID_INVALIDATED - never silent. */
    | { readonly type: 'INVALIDATE_LOT_MAX_BIDS'; readonly lotId: string; readonly reason: string }
    /**
     * ENGINE-ISSUED ONLY. A proxy alternation is resolved one increment at a
     * time and emitted as real BID_ACCEPTED events (auditability). A very wide
     * gap on a fine ladder can produce hundreds of steps - more than a single
     * durable commit may hold (DynamoDB TransactWriteItems caps at 100 items).
     * The reducer therefore caps proxy steps PER DECISION; when more remain the
     * engine issues this to finish the alternation, one bounded commit at a
     * time. Never sent by a client (system actor only). See `pendingProxyLots`. */
    | { readonly type: 'CONTINUE_PROXY_RESOLUTION'; readonly lotId: string }
    // ---- ADR-42: material announcements ----
    /**
     * A lot (or sale-wide, lotId null) announcement - "this horse is a cribber",
     * "papers will not transfer as catalogued", a vet disclosure, a catalog
     * correction. NOT an edit to description text: an immutable, sequenced,
     * public event in the ring log. MATERIAL severity applies the configured
     * policy to the lot's active max bids (preserve / invalidate / reconfirm) -
     * any invalidation is itself an event. Clerk only (legally significant).
     */
    | {
        readonly type: 'POST_ANNOUNCEMENT';
        readonly lotId: string | null;
        readonly text: string;
        readonly severity: AnnouncementSeverity;
        /** Free-form category: 'VET' | 'PAPERS' | 'CATALOG_CORRECTION' | 'SELLER_REP' | ... */
        readonly category: string;
        /** Seq of an earlier announcement this one corrects/supersedes. */
        readonly supersedesSeq?: number;
        /** ADR-60 cut 3: bidders must acknowledge before bidding. Defaults to
         *  `policy.announcements.requireAckOnMaterial` for MATERIAL, false
         *  for INFO. */
        readonly requiresAcknowledgement?: boolean;
      }
    /**
     * ADR-60 cut 3: the bidder has read the announcements on `lotId` (null =
     * sale-wide) up to and including `throughSeq`. Recorded once per scope as
     * a high-water mark; a later announcement needs a new acknowledgement.
     */
    | { readonly type: 'ACKNOWLEDGE_ANNOUNCEMENTS'; readonly lotId: string | null; readonly throughSeq: number }
    // ---- ADR-42: TIMED mode ----
    /**
     * Open a lot for timed bidding with an AUTHORITATIVE scheduled close. The
     * control plane computes `closesAt` (+ `closeGroupId` for cascading groups)
     * with `scheduleTimedCloses`; the reducer only ever sees the concrete time.
     * Close is decided by engine `now` vs `closesAt` - never by a browser clock
     * and never by timer delivery (the TICK sweep runs before every command).
     * Clerk (auction manager) only; TIMED mode only.
     */
    | {
        readonly type: 'OPEN_TIMED_LOT';
        readonly lotId: string;
        readonly openingAsk: number;
        readonly closesAt: number;
        readonly pricingMode: PricingMode;
        /** Whole dollars; null/omitted = no reserve. Never emitted publicly. */
        readonly reserve?: number | null;
        readonly closeGroupId?: string | null;
      }
    /**
     * ADR-70 cut 6i (hitlist 17.4): timed recovery. A lot that closed with no
     * sale opens again with a fresh close, its board as it left (the high bid
     * stands, maxes still standing run again). Never a sold lot: reversing a
     * sale is VOID_SALE. `closeSeq` is the TIMED_LOT_CLOSED this reopen
     * answers, so a redelivered or late request never reopens a lot that has
     * since reopened or closed again. Control plane only (the office's
     * recovery, sent by the worker); `requestedBy` is the staffer who asked.
     */
    | {
        readonly type: 'REOPEN_TIMED_LOT';
        readonly lotId: string;
        readonly closeSeq: number;
        readonly closesAt: number;
        readonly closeGroupId: string | null;
        readonly recoveryId: string;
        readonly reason: string;
        readonly requestedBy: string;
      }
  );

export type AnnouncementSeverity = 'INFO' | 'MATERIAL';

/** Why a lot was disposed. Timed closes never masquerade as a hammer. */
export type DispositionCause = 'HAMMER' | 'TIMED_CLOSE';
export type NoSaleCause = 'CLERK' | 'NO_BIDS' | 'RESERVE_NOT_MET';

// ---------------------------------------------------------------------------
// Events - spec section 8
// ---------------------------------------------------------------------------

export type Channel = 'public' | 'private' | 'floor';

interface EventBase {
  /** Assigned by the reducer from state.nextSeq - deterministic. `eventId` is
   *  stamped by the engine at commit time, since a ULID needs time and
   *  randomness that this package deliberately does not have. */
  readonly seq: number;
  readonly channel: Channel;
  readonly actorId: string;
  readonly serverTs: number;
  /** ADR-70 cut 4 (hitlist section 36): the role the actor acted in, stamped
   *  by the engine at commit like `eventId` (the reducer never sets it).
   *  Absent on events committed before cut 4. Never on the public wire. */
  readonly actorRole?: AuditActorRole;
  /** ADR-70 cut 4: the operator action this event answers (the command's
   *  `clientActionId`), stamped by the engine at commit. Absent for commands
   *  sent without an id (bids, machine commands) and before cut 4. Never on
   *  the public wire. */
  readonly clientActionId?: string;
  /** ADR-70 cut 6b (hitlist 8.2): where the command behind this event came
   *  from, stamped by the engine at commit like `actorRole`. MAX_BID on a bid
   *  the engine placed from a standing max. Absent for the system's events,
   *  an event naming someone other than the command's actor, a client that
   *  did not say, and before 6b. Never on the public wire. */
  readonly origin?: BidOrigin;
  /** ADR-70 cut 6b: the class of device the command came from, as the ring
   *  read it from the connection. Absent wherever `origin` would be absent
   *  for the same reasons, and on a bid the engine placed (no device placed
   *  it). Never on the public wire. */
  readonly deviceClass?: DeviceClass;
}

/**
 * ADR-70 cut 4: who an event's actor was, as the permanent record says it.
 * The ring roles, plus the two actors no person stands behind:
 *   system  the engine or the control plane acting on its own (a timed close,
 *           an approval arriving from the queue, the floor-input watchdog)
 *   proxy   the engine bidding on a bidder's standing max; the actor id is
 *           that bidder, the hand that placed the bid is the engine's
 */
export type AuditActorRole = ActorRole | 'system' | 'proxy';

export type DomainEvent = EventBase &
  (
    | { readonly type: 'CURRENT_LOT_SET'; readonly lotId: string }
    | {
        readonly type: 'LOT_OPENED';
        readonly lotId: string;
        readonly openingAsk: number;
        readonly incrementTableId: string;
        /** ADR-60 cut 5: the group this lot leads, when it sells with others. */
        readonly group?: SellingGroup;
      }
    /** ADR-60 cut 5: a CHOICE winner took some of the group; these go back to
     *  UPCOMING for the clerk to re-offer. Public. */
    | { readonly type: 'LOT_GROUP_REMAINDER'; readonly lotIds: readonly string[]; readonly soldLotIds: readonly string[] }
    | {
        readonly type: 'BID_ACCEPTED';
        readonly lotId: string;
        readonly bidAmount: number;
        readonly bidSource: BidSource;
        readonly bidderRef: string;
        readonly bidderId: string;
        readonly paddleNumber: string | null;
        readonly previousHighBid: number | null;
        readonly newHighBid: number;
        readonly nextAsk: number;
        readonly lotState: LotState;
        /** For bidSource PROXY: the seq of the MAX_BID_PLACED this bid executed
         *  on behalf of - provenance from max → bid → LOT_SOLD. Private. */
        readonly proxyOfMaxSeq?: number | null;
      }
    | { readonly type: 'ASK_SET'; readonly lotId: string; readonly ask: number; readonly reason: string | null }
    // ---- ADR-42: max (proxy) bids - all PRIVATE channel; `maxAmount` is also
    //      redacted from the FLOOR channel (the auctioneer never sees a max) ----
    | {
        readonly type: 'MAX_BID_PLACED';
        readonly lotId: string;
        readonly bidderId: string;
        readonly maxAmount: number;
        /** Seq of the max this one replaces (raise/lower), or null. */
        readonly replacesSeq: number | null;
      }
    | { readonly type: 'MAX_BID_CANCELLED'; readonly lotId: string; readonly bidderId: string; readonly maxSeq: number }
    | {
        readonly type: 'MAX_BID_INVALIDATED';
        readonly lotId: string;
        readonly bidderId: string;
        readonly maxSeq: number;
        /** Why: 'MATERIAL_ANNOUNCEMENT:<seq>', 'BIDDER_REVOKED', 'CLERK:<reason>', ... */
        readonly reason: string;
        /** Policy asked the bidder to re-confirm (re-place) rather than a hard drop. */
        readonly reconfirmRequested: boolean;
      }
    /** The max can no longer meet the ask (or its limit/approval blocks it), or
     *  (ADR-60) the operator declined the step it was pending on. Emitted once. */
    | { readonly type: 'PROXY_EXHAUSTED'; readonly lotId: string; readonly bidderId: string; readonly maxSeq: number; readonly atAsk: number; readonly reason: 'MAX_REACHED' | 'BID_LIMIT' | 'NOT_APPROVED' | 'DECLINED' }
    // ---- ADR-60: operator-accepted online bids - PUBLIC (the crowd sees a
    //      bid is waiting; identity is redacted like any bid) ----
    | {
        readonly type: 'ONLINE_BID_PENDING';
        readonly pendingBidId: string;
        readonly lotId: string;
        readonly amount: number;
        /** ONLINE, or PROXY for a max stepping in (masked to ONLINE publicly). */
        readonly bidSource: BidSource;
        readonly bidderRef: string;
        readonly bidderId: string;
        readonly proxyOfMaxSeq: number | null;
        readonly expiresAt: number | null;
      }
    | {
        readonly type: 'PENDING_BID_RESOLVED';
        readonly pendingBidId: string;
        readonly lotId: string;
        readonly amount: number;
        readonly bidderRef: string;
        readonly bidderId: string;
        readonly result: PendingResolution;
        /** The operator's stated reason on a decline (REJECTED by hand), or
         *  null. Part of the immutable record: a dispute over a real-money
         *  bid needs who declined it (actorId), when (serverTs) and why. */
        readonly reason: string | null;
      }
    | { readonly type: 'CONFIRM_MODE_SET'; readonly enabled: boolean; readonly reason: string | null }
    // ---- ADR-60: bidder session fencing - PRIVATE ----
    | { readonly type: 'BIDDING_SESSION_TRANSFERRED'; readonly bidderId: string; readonly sessionId: string; readonly previousSessionId: string | null }
    /**
     * ADR-42 LIVE_WITH_PREBIDDING: marks the moment a lot with standing pre-bid
     * maxes enters the ring. Emitted inside OPEN_LOT, BEFORE the proxies run, so
     * the log reads: LOT_OPENED → PREBID_HANDOFF → BID_ACCEPTED(PROXY)… Private
     * (floor sees it; the COUNT of maxes is not for the public stream).
     */
    | { readonly type: 'PREBID_HANDOFF'; readonly lotId: string; readonly activeMaxCount: number; readonly openingAsk: number }
    // ---- ADR-42: material announcements - PUBLIC (bidders must see them) ----
    | {
        readonly type: 'ANNOUNCEMENT_POSTED';
        readonly lotId: string | null;
        readonly text: string;
        readonly severity: AnnouncementSeverity;
        readonly category: string;
        readonly supersedesSeq: number | null;
        /** What the configured policy did to the lot's active maxes (MATERIAL only). */
        readonly maxBidPolicyApplied: 'NONE' | 'PRESERVE_MAX_BIDS' | 'INVALIDATE_MAX_BIDS' | 'REQUIRE_RECONFIRM';
        /** ADR-60 cut 3: bidders must acknowledge this before bidding. */
        readonly requiresAcknowledgement?: boolean;
      }
    /** ADR-60 cut 3: a bidder's acknowledgement (private; the floor sees it). */
    | { readonly type: 'ANNOUNCEMENT_ACKNOWLEDGED'; readonly bidderId: string; readonly lotId: string | null; readonly throughSeq: number; readonly sessionId: string | null }
    // ---- ADR-42: TIMED mode - all PUBLIC; the reserve is never emitted ----
    | {
        readonly type: 'TIMED_LOT_OPENED';
        readonly lotId: string;
        readonly openingAsk: number;
        /** The authoritative scheduled close this lot was opened with. */
        readonly closesAt: number;
        readonly pricingMode: PricingMode;
        readonly closeGroupId: string | null;
        readonly incrementTableId: string;
      }
    /** A bid inside the soft-close window moved the close. Every move is an
     *  event, so the history (8:00 → 8:02 → 8:04) is provable from the log. */
    | {
        readonly type: 'LOT_CLOSE_EXTENDED';
        readonly lotId: string;
        readonly previousCloseAt: number;
        readonly newCloseAt: number;
        /** The accepted bid (manual or PROXY) that triggered it. */
        readonly triggeringBidSeq: number;
        readonly extensionNumber: number;
        readonly policy: { readonly extensionWindowMs: number; readonly extensionDurationMs: number; readonly extendFrom: 'BID_TIME' | 'SCHEDULED_CLOSE' };
        /** Set when this lot extended because a sibling in its close group did. */
        readonly cascadedFromLotId: string | null;
      }
    /**
     * ADR-70 cut 6i (hitlist 17.4): a lot that closed with no sale is open
     * again, closing at `closesAt` (its extensions start over). The board as
     * it comes back rides along, as on LOT_REOPENED, so a client folding
     * events need not wait for the snapshot. Public; the office's reason, the
     * recovery id and who asked are the log's only (redact.ts).
     */
    | {
        readonly type: 'TIMED_LOT_REOPENED';
        readonly lotId: string;
        readonly closesAt: number;
        readonly closeGroupId: string | null;
        /** The close this reopen answers: its TIMED_LOT_CLOSED seq and time. */
        readonly reopensCloseSeq: number;
        readonly previousCloseAt: number;
        readonly from: 'NO_SALE';
        readonly restoredHighBid: number | null;
        readonly restoredAsk: number | null;
        readonly restoredBidSource: BidSource | null;
        readonly restoredBidderRef: string | null;
        readonly recoveryId: string;
        readonly reason: string;
        readonly requestedBy: string;
      }
    /** The authoritative close committed. Disposition follows (same tick when
     *  policy.autoDisposition, else CLOSED_PENDING awaits staff). */
    | {
        readonly type: 'TIMED_LOT_CLOSED';
        readonly lotId: string;
        readonly scheduledCloseAt: number;
        readonly extensions: number;
        readonly finalHighBid: number | null;
        readonly finalBidderRef: string | null;
        readonly finalBidSource: BidSource | null;
      }
    | { readonly type: 'FAIR_WARNING'; readonly lotId: string }
    /** ADR-70 cut 3: a close step called (or, `step: null`, withdrawn back to
     *  open bidding). Public: bidders see "going once" as it is said. A bid
     *  clearing the step needs no event of its own: every BID_ACCEPTED means
     *  "open, no step". */
    | { readonly type: 'CLOSE_STEP_SET'; readonly lotId: string; readonly step: CloseStep | null; readonly previousStep: CloseStep | null }
    | {
        readonly type: 'HAMMER';
        readonly lotId: string;
        readonly finalBid: number;
        readonly winningBidderRef: string;
        readonly bidSource: BidSource;
      }
    | {
        readonly type: 'LOT_SOLD';
        readonly lotId: string;
        readonly price: number;
        /** The raw high-bidder id: the CLERK's actor id for a floor sale, the
         *  bidder's Cognito sub for an online sale - NOT necessarily the buyer.
         *  Attribution keys off winningPaddleNumber (floor) or this (online). */
        readonly buyerId: string;
        readonly bidSource: BidSource;
        readonly pricingMode: PricingMode;
        /** M6: the buyer's paddle for a floor sale (→ registration.live_paddle),
         *  null online. Redacted on the public channel - settlement only. */
        readonly winningPaddleNumber: string | null;
        /** M6: sequence of the winning accepted bid, for provenance/audit. */
        readonly winningBidSeq: number | null;
        /** ADR-42: how the lot was disposed. Absent/HAMMER = live clerk; TIMED_CLOSE
         *  = authoritative scheduled close. Settlement consumes LOT_SOLD either way. */
        readonly dispositionCause?: DispositionCause;
        /** ADR-60 cut 5: present when the lot sold as part of a group. `lotId`
         *  and `price` keep their single-lot meaning (the lead, the unit
         *  price) so every existing consumer stays right; settlement reads
         *  the group for the other lots and the total. */
        readonly group?: SoldGroup;
      }
    | { readonly type: 'LOT_NO_SALE'; readonly lotId: string; readonly cause?: NoSaleCause }
    | { readonly type: 'LOT_PASSED'; readonly lotId: string }
    | {
        readonly type: 'LOT_REOPENED';
        readonly lotId: string;
        readonly reason: string;
        /** ADR-70 cut 6c: what the lot was reopened from. Absent before 6c,
         *  when it was always the hammer. */
        readonly from?: 'HAMMERED' | 'PASSED' | 'NO_SALE';
        /** ADR-70 cut 6c, on a reopen from PASSED or NO_SALE: the board as it
         *  comes back, so a client folding events need not wait for the
         *  snapshot. `restoredBidSource` is masked like any bid source. */
        readonly restoredHighBid?: number | null;
        readonly restoredAsk?: number | null;
        readonly restoredBidSource?: BidSource | null;
        readonly restoredBidderRef?: string | null;
      }
    /**
     * ADR-70 cut 6c (hitlist 17.1): the standing bid `reversesSeq` was taken
     * back. That BID_ACCEPTED stays in the log; this is the compensating
     * fact. Public, so the crowd sees the board go back; the bidder's id and
     * the clerk's reason are floor and log only (redact.ts).
     */
    | {
        readonly type: 'BID_RETRACTED';
        readonly lotId: string;
        readonly reversesSeq: number;
        readonly reason: string;
        readonly amount: number;
        readonly bidSource: BidSource;
        readonly bidderRef: string;
        readonly bidderId: string;
        readonly restoredHighBid: number | null;
        readonly restoredAsk: number | null;
        /** The source of the bid that stands again, so a display can say
         *  "$10,000 - Floor" without a snapshot (PROXY masked publicly). */
        readonly restoredBidSource: BidSource | null;
      }
    /**
     * ADR-70 cut 6c (hitlist 17.2): the lot's bidding was reset: no standing
     * bid, the ask restarts at `ask`. Every earlier bid on the lot stays in
     * the log. Public (Q14: the bidder page says the bidding was reset); the
     * clerk's reason is floor and log only.
     */
    | {
        readonly type: 'BIDDING_RESET';
        readonly lotId: string;
        readonly reason: string;
        readonly ask: number;
        /** The standing bid the reset cleared, or null when there was none. */
        readonly resetHighBid: number | null;
        readonly resetBidSeq: number | null;
        /** The operator asked to end the lot's max bids too. */
        readonly clearMaxBids: boolean;
        /** How many active maxes ended (each its own MAX_BID_INVALIDATED). */
        readonly clearedMaxBids: number;
      }
    /**
     * ADR-70 cut 6d: the lot in the ring bids up by `step` (null = the sale's
     * table again). Public, so a bidder sees why the ask moved. `ask` is the
     * ask after the change; it equals `previousAsk` when the board held no
     * bid (the opening ask stands).
     */
    | {
        readonly type: 'INCREMENT_CHANGED';
        readonly lotId: string;
        readonly step: number | null;
        readonly previousStep: number | null;
        readonly ask: number | null;
        readonly previousAsk: number | null;
      }
    /**
     * ADR-70 cut 6d: the lot left the ring with no result and stays in the
     * sale. Public; the reason, when given, is floor and log only. `group`
     * names every lot when a group was up.
     */
    | {
        readonly type: 'LOT_REMOVED_FROM_RING';
        readonly lotId: string;
        readonly reason: string | null;
        readonly group?: { readonly lotIds: readonly string[] };
      }
    /**
     * ADR-70 cut 2: the winner of an equal bid changed (brief section 12).
     * Public, so the crowd and the displaced bidder learn the board's
     * holder changed and to which side; who, paddles and the reason are
     * floor and log only (redact.ts). An award is followed in the same
     * commit by the winner's BID_ACCEPTED at the same amount; a restore
     * (`restoresSeq` set) moves the bid back to the earlier online bid,
     * `winnerBidSeq`, without a new bid.
     */
    | {
        readonly type: 'EQUAL_BID_OVERRIDDEN';
        readonly lotId: string;
        readonly amount: number;
        readonly awardedTo: 'ONSITE' | 'ONLINE';
        /** OPERATOR: a clerk's command. POLICY: the ring's equal-bid policy decided. */
        readonly cause: 'OPERATOR' | 'POLICY';
        readonly policy: EqualBidPolicy;
        /** The bid that held the board before (the listing version the decision was made on). */
        readonly displacedBidSeq: number;
        readonly displacedBidSource: BidSource;
        readonly displacedBidderRef: string;
        readonly displacedBidderId: string;
        readonly displacedPaddle: string | null;
        /** The bid that holds it now. */
        readonly winnerBidSeq: number;
        readonly winnerBidSource: BidSource;
        readonly winnerBidderRef: string;
        readonly winnerBidderId: string;
        readonly winnerPaddle: string | null;
        /** A restore: the EQUAL_BID_OVERRIDDEN it reverses. */
        readonly restoresSeq: number | null;
        readonly reason: string;
      }
    | {
        readonly type: 'FLOOR_BID_CORRECTED';
        readonly lotId: string;
        /** The bid event this reverses. That event remains in the log - this is
         *  a compensating fact, not an edit. */
        readonly reversesSeq: number;
        readonly reason: string;
        readonly restoredHighBid: number | null;
        readonly restoredAsk: number | null;
      }
    | { readonly type: 'ONLINE_PAUSED'; readonly reason: PauseReason }
    | { readonly type: 'ONLINE_RESUMED' }
    | {
        readonly type: 'BIDDER_APPROVAL_CHANGED';
        readonly bidderId: string;
        readonly approvalStatus: ApprovalStatus;
        readonly approvalVersion: number;
      }
    /** ADR-70 cut 5f: the ring staff roster changed. Private; the ring sends
     *  it to no socket. It is in the log so the archive (cut 4) says who was
     *  assigned to which ring, and when. */
    | {
        readonly type: 'RING_STAFF_CHANGED';
        readonly version: number;
        readonly assignments: Readonly<Record<string, readonly string[]>>;
      }
    /** ADR-70 cut 6e: the tenant's live-bidding capabilities changed, as the
     *  control plane sent them. Private; in the log so the archive says when
     *  operators could and could not see max bids (and, from 6f, bidder
     *  intent: the switch is recorded, the signals never are). */
    | {
        readonly type: 'RING_CAPABILITIES_CHANGED';
        readonly version: number;
        readonly operatorMaxBidVisibility: boolean;
        readonly operatorIntentSignals?: boolean;
        readonly intentControls?: IntentControls;
      }
    /**
     * ADR-70 cut 6e (hitlist 9.2, its audit fields): an operator looked at the
     * max bids standing on a lot. Private. `viewed` holds what was shown, one
     * entry per active max, each field present only when the viewer's role
     * may see it; the floor wire drops `viewed` (redact.ts), so other
     * operators see that a look happened and how many maxes stood, never the
     * figures. The viewer's own socket is sent `viewed` directly.
     */
    | {
        readonly type: 'MAX_BID_VIEWED';
        readonly lotId: string;
        readonly viewerRole: MaxBidViewerRole;
        readonly scopes: readonly MaxBidViewScope[];
        readonly activeCount: number;
        readonly viewed: readonly ViewedMaxBid[];
      }
    /** Terminal settlement marker: the ring is sealed at `finalSeq` (this event's
     *  own seq) and accepts nothing further. Public channel (no PII beyond the
     *  redacted actorId) so observers see the sale has ended. */
    | { readonly type: 'SALE_FINALIZED'; readonly finalSeq: number }
    /**
     * ADR-65: a sale this ring made no longer stands. Public, because the
     * crowd watched the sale happen and a board that keeps saying SOLD is the
     * lie; redacted for the public wire exactly like LOT_SOLD (buyerId,
     * winningPaddleNumber, actorId) plus `reason`, which is dispute and
     * business information and never travels publicly (decision 7). The
     * settlement projector reads the store, so it sees every field.
     */
    | {
        readonly type: 'SALE_VOIDED';
        readonly lotId: string;
        /** The LOT_SOLD this reverses. */
        readonly soldSeq: number;
        readonly kind: SaleReversalKind;
        readonly reason: string;
        readonly price: number;
        readonly bidSource: BidSource;
        readonly buyerRef: string;
        readonly buyerId: string;
        readonly winningPaddleNumber: string | null;
        /** Present when the reversed sale was a group sale: every lot it covered. */
        readonly group?: { readonly lotIds: readonly string[] };
      }
  );

/** ADR-65: why a sale was reversed at the ring. The ledger's vocabulary
 *  (purchase_reversal.kind); BUYER_DEFAULT and BUYER_CORRECTION are recorded
 *  through the office door and a later cut, never by this command. */
export type SaleReversalKind = 'VOID' | 'TURN_BACK';

// ---------------------------------------------------------------------------
// Outcomes - spec section 7
// ---------------------------------------------------------------------------

export type RejectionReason =
  | 'ASK_CHANGED'
  | 'OUTBID_IN_FLIGHT'
  | 'LOT_NOT_OPEN'
  | 'POST_HAMMER'
  | 'NOT_APPROVED'
  | 'ONLINE_PAUSED'
  | 'SESSION_NOT_ACTIVE'
  | 'INVALID_INCREMENT'
  | 'BID_LIMIT_EXCEEDED'
  | 'IDEMPOTENCY_KEY_REUSE'
  /** A bid (or an ask) that does not beat the current high bid. The high bid
   *  only ever goes up; the ask can be CUT below the ladder, never below the
   *  board. */
  | 'BELOW_HIGH_BID'
  // Non-bid command rejections
  | 'NOT_AUTHORIZED'
  | 'ILLEGAL_TRANSITION'
  | 'CORRECTION_REQUIRES_RECONCILIATION'
  /** The sale has been finalized for settlement; the ring is sealed and accepts
   *  no further commands. */
  | 'SALE_FINALIZED'
  // ---- ADR-42: max (proxy) bids ----
  /** Tenant policy has proxy bidding off, or the lot is outside the proxy scope. */
  | 'PROXY_DISABLED'
  /** A max below the current ask (or below the bidder's own standing high bid)
   *  could never execute - refused rather than stored as a dead instruction. */
  | 'MAX_BELOW_ASK'
  /** CANCEL_MAX_BID with no active max on that lot. */
  | 'NO_ACTIVE_MAX'
  /** TIMED: the lot's authoritative close has already committed (the bid
   *  arrived after scheduledCloseAt, whether or not a timer had fired). */
  | 'LOT_CLOSED'
  // ---- ADR-60 ----
  /** ACCEPT / DECLINE named a pending bid the ring does not hold (it was
   *  already resolved, or the id is stale). */
  | 'NO_PENDING_BID'
  /** A recorded operational change (confirm mode mid-sale) needs a reason. */
  | 'REASON_REQUIRED'
  /** ADR-60 cut 3: a material update on this lot has not been acknowledged
   *  by this bidder. The bid is refused until they read it. */
  | 'ACK_REQUIRED'
  /** ADR-60 cut 5: a CHOICE group cannot be confirmed sold without the
   *  winner's selection (one or more lots of the group). */
  | 'SELECTION_REQUIRED'
  // ---- ADR-65 ----
  /** VOID_SALE named a sale the ring does not hold: no sale of that lot, or a
   *  different soldSeq (a stale console voiding a sale it has not seen). */
  | 'NO_SUCH_SALE'
  /** The named sale was already reversed. */
  | 'SALE_ALREADY_VOIDED'
  /** OPEN_LOT (or OPEN_TIMED_LOT) on a lot whose sale still stands. Nothing
   *  sells a lot twice without a recorded reversal in between (R2). */
  | 'LOT_ALREADY_SOLD'
  /** ADR-70 cut 3: SET_CURRENT_LOT while the lot in the ring holds a bid
   *  (open with a high bid, or hammered and not yet settled). Leaving it
   *  would strand the bidder; the operator sells, passes or no-sales first. */
  | 'LOT_IN_PROGRESS'
  // ---- ADR-70 ----
  /** A clerk command was decided on a screen the ring has moved past: it named
   *  a lot that is not in the ring, or its `expected` view (lot, high-bid seq,
   *  held bid, ask) no longer matches. Nothing changed; the console reloads
   *  the snapshot and the operator presses again on what is really there. */
  | 'STALE_AUCTION_STATE'
  /** ADR-70 cut 2: the ring's equal-bid policy does not allow this override. */
  | 'EQUAL_BID_POLICY'
  /** ADR-70 cut 2: nothing to override: the standing bid is not an online
   *  bid (award), or no award of this lot stands (restore). */
  | 'NO_EQUAL_BID'
  /** ADR-70 cut 6e: the tenant capability behind the command is off (only a
   *  HipNumber / LotNumber Super Admin switches it on). */
  | 'CAPABILITY_OFF';

/** The result of applying one command. `rejected` never mutates state - a
 *  rejected command is a fact about a request, not a change to the ring. */
export type Decision =
  | {
      readonly status: 'accepted';
      readonly state: RingState;
      readonly events: readonly DomainEvent[];
    }
  | {
      readonly status: 'rejected';
      readonly reason: RejectionReason;
      /** Current state echoed back so a client converges without a round trip
       *  (spec 8.3). */
      readonly state: RingState;
    };
